import { useState } from "react";
import { observer } from "@legendapp/state/react";
import InfillionLogo from "@/components/InfillionLogo";
import InfillionSpinnerLogo from "@/components/InfillionSpinnerLogo";
import NavItem from "@/layouts/AppLayout/NavItem";
import ProfileMenu from "@/layouts/AppLayout/ProfileMenu";
import { ROUTES } from "@/constants/routes";
import { TEXT } from "@/constants/text";
import { isGlobalAdmin } from "@/stores/userStore";
import styles from "@/layouts/AppLayout/Sidebar.module.css";

const CampaignOverviewIcon = (
  <svg
    fill="none"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="16"
  >
    <line x1="18" x2="18" y1="20" y2="10" />
    <line x1="12" x2="12" y1="20" y2="4" />
    <line x1="6" x2="6" y1="20" y2="14" />
  </svg>
);

const UserManagementIcon = (
  <svg
    fill="none"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="16"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const NAV_ITEMS = [
  {
    icon: CampaignOverviewIcon,
    label: TEXT.sidebar.unifiedInsights,
    to: "/",
  },
];

const ADMIN_NAV_ITEMS = [
  {
    icon: UserManagementIcon,
    label: TEXT.sidebar.userManagement,
    to: ROUTES.ADMIN_USERS,
  },
];

const Sidebar = observer(() => {
  // Opens expanded; the toggle collapses it to the 72px icon-only rail.
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`${styles.sidebar}${collapsed ? ` ${styles.collapsed}` : ""}`}
    >
      <div className={styles.logoBlock}>
        <div aria-hidden={collapsed} className={styles.wordmark}>
          <InfillionLogo aria-hidden="true" />
          <div className={styles.portalLabel}>{TEXT.sidebar.portalLabel}</div>
        </div>
        <InfillionSpinnerLogo
          aria-hidden={!collapsed}
          aria-label={TEXT.sidebar.logoLabel}
          className={styles.spinner}
          role="img"
        />
        <button
          aria-label={collapsed ? TEXT.sidebar.expand : TEXT.sidebar.collapse}
          className={styles.toggleButton}
          type="button"
          onClick={() => setCollapsed((c) => !c)}
        >
          <i
            aria-hidden="true"
            className={`pi ${collapsed ? "pi-angle-double-right" : "pi-angle-double-left"}`}
          />
        </button>
      </div>

      <nav className={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <NavItem
            key={item.label}
            collapsed={collapsed}
            icon={item.icon}
            label={item.label}
            to={item.to}
          />
        ))}
        {isGlobalAdmin() && (
          <>
            <div
              aria-hidden={collapsed}
              className={`${styles.sectionLabel}${collapsed ? ` ${styles.sectionLabelCollapsed}` : ""}`}
            >
              {TEXT.sidebar.adminSection}
            </div>
            {ADMIN_NAV_ITEMS.map((item) => (
              <NavItem
                key={item.label}
                collapsed={collapsed}
                icon={item.icon}
                label={item.label}
                to={item.to}
              />
            ))}
          </>
        )}
      </nav>

      <ProfileMenu collapsed={collapsed} />
    </aside>
  );
});

export default Sidebar;
